<!--
 * @Description: 3D照片墙组件 - 酷炫科技感，支持3D效果和图片预览
 * @Version: 1.0
 * @Author: AI Assistant
 * @Date: 2024-01-01 00:00:00
-->
<template>
    <div class="photo-wall-3d-container">
      <!-- 3D照片墙主体 -->
      <div 
        class="photo-wall-3d" 
        ref="photoWall"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
        @click="handleContainerClick"
      >
        <div 
          v-for="(photo, index) in photos" 
          :key="photo.id || index"
          class="photo-item"
          :class="{ 'active': selectedIndex === index }"
          :style="getPhotoStyle(index)"
          @click="handlePhotoClick($event, index)"
          @mouseenter="hoverPhoto(index)"
          @mouseleave="leavePhoto(index)"
        >
          <div class="photo-frame">
            <img 
              :src="photo.src" 
              :alt="photo.alt || `Photo ${index + 1}`"
              class="photo-image"
            />
            <div class="photo-overlay">
              <div class="photo-title">{{ photo.title || `图片 ${index + 1}` }}</div>
              <div class="photo-description">{{ photo.description || '点击查看大图' }}</div>
            </div>
            <div class="photo-glow"></div>
          </div>
        </div>
      </div>
  
      <!-- 图片预览模态框 -->
      <div 
        v-if="showPreview" 
        class="preview-modal"
        @click="closePreview"
      >
        <div class="preview-content" @click.stop>
          <button class="close-btn" @click="closePreview">&times;</button>
          <button class="nav-btn prev-btn" @click="prevPhoto" v-if="photos.length > 1">‹</button>
          <button class="nav-btn next-btn" @click="nextPhoto" v-if="photos.length > 1">›</button>
          
          <div class="preview-image-container">
            <img 
              :src="currentPhoto.src" 
              :alt="currentPhoto.alt || 'Preview'"
              class="preview-image"
            />
          </div>
          
          <div class="preview-info">
            <h3 class="preview-title">{{ currentPhoto.title || `图片 ${selectedIndex + 1}` }}</h3>
            <p class="preview-description">{{ currentPhoto.description || '暂无描述' }}</p>
            <div class="preview-counter">{{ selectedIndex + 1 }} / {{ photos.length }}</div>
          </div>
        </div>
      </div>
  
      <!-- 科技感背景效果 -->
      <div class="tech-background">
        <div class="tech-line" v-for="n in 20" :key="n" :style="getTechLineStyle(n)"></div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'PhotoWall3D',
    props: {
      photos: {
        type: Array,
        required: true,
        default: () => []
      },
      autoRotate: {
        type: Boolean,
        default: true
      },
      rotationSpeed: {
        type: Number,
        default: 0.5
      }
    },
    data() {
      return {
        selectedIndex: 0,
        showPreview: false,
        mouseX: 0,
        mouseY: 0,
        rotationX: 0,
        rotationY: 0,
        hoveredIndex: -1,
        animationId: null,
        resizeTimer: null,
        isMouseOver: false
      }
    },
    computed: {
      currentPhoto() {
        return this.photos[this.selectedIndex] || {}
      }
    },
    mounted() {
      this.startAnimation()
      // 确保在DOM渲染完成后重新计算布局
      this.$nextTick(() => {
        this.updateLayout()
      })
      
      // 监听窗口大小变化
      window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy() {
      this.stopAnimation()
      window.removeEventListener('resize', this.handleResize)
    },
    methods: {
      getPhotoStyle(index) {
        const total = this.photos.length
        const angle = (index / total) * Math.PI * 2
        // 根据容器大小动态调整半径，确保不超出边界
        const containerWidth = this.$refs.photoWall ? this.$refs.photoWall.offsetWidth : 800
        const containerHeight = this.$refs.photoWall ? this.$refs.photoWall.offsetHeight : 600
        const radius = Math.min(containerWidth, containerHeight) * 0.3 // 使用容器尺寸的30%作为半径，适应更大的图片
        
        const x = Math.cos(angle) * radius
        const z = Math.sin(angle) * radius
        
        // 添加鼠标交互效果
        let extraX = 0
        let extraY = 0
        let extraZ = 0
        
        if (this.hoveredIndex === index) {
          extraZ = 40
          extraY = -20
        }
        
        // 计算z-index，基于3D空间中的实际深度
        const depth = z + extraZ
        const zIndex = this.hoveredIndex === index ? 2000 : (1000 + Math.max(0, depth + 200))
        
        return {
          transform: `translate3d(${x + extraX}px, ${extraY}px, ${z + extraZ}px) rotateY(${angle * 180 / Math.PI}deg)`,
          zIndex: zIndex
        }
      },
      
      getTechLineStyle(n) {
        const delay = n * 0.1
        const duration = 3 + Math.random() * 2
        return {
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }
      },
      
      selectPhoto(index) {
        console.log('点击图片:', index, this.photos[index])
        this.selectedIndex = index
        this.showPreview = true
        this.$emit('photo-selected', this.photos[index], index)
      },
      
      handlePhotoClick(event, index) {
        event.stopPropagation()
        event.preventDefault()
        console.log('处理图片点击:', index)
        
        // 检查点击是否在图片的有效区域内
        const rect = event.currentTarget.getBoundingClientRect()
        const clickX = event.clientX - rect.left
        const clickY = event.clientY - rect.top
        
        // 确保点击在图片范围内
        if (clickX >= 0 && clickX <= rect.width && clickY >= 0 && clickY <= rect.height) {
          this.selectPhoto(index)
        }
      },
      
      hoverPhoto(index) {
        this.hoveredIndex = index
      },
      
      leavePhoto(index) {
        this.hoveredIndex = -1
      },
      
      closePreview() {
        this.showPreview = false
      },
      
      nextPhoto() {
        this.selectedIndex = (this.selectedIndex + 1) % this.photos.length
      },
      
      prevPhoto() {
        this.selectedIndex = (this.selectedIndex - 1 + this.photos.length) % this.photos.length
      },
      
      handleMouseMove(event) {
        this.isMouseOver = true
        const rect = this.$refs.photoWall.getBoundingClientRect()
        this.mouseX = (event.clientX - rect.left - rect.width / 2) / rect.width
        this.mouseY = (event.clientY - rect.top - rect.height / 2) / rect.height
        
        // 限制旋转角度范围，避免过度旋转
        this.rotationX = Math.max(-15, Math.min(15, this.mouseY * 15))
        this.rotationY = Math.max(-30, Math.min(30, this.mouseX * 30))
      },
      
      handleMouseLeave() {
        this.isMouseOver = false
        // 平滑回到中心位置
        this.rotationX = 0
        this.rotationY = 0
      },
      
      startAnimation() {
        if (!this.autoRotate) return
        
        const animate = () => {
          // 只在没有鼠标悬停时进行自动旋转
          if (!this.isMouseOver) {
            this.rotationY += this.rotationSpeed
          }
          this.animationId = requestAnimationFrame(animate)
        }
        animate()
      },
      
      stopAnimation() {
        if (this.animationId) {
          cancelAnimationFrame(this.animationId)
          this.animationId = null
        }
      },
      
      updateLayout() {
        // 强制重新计算布局
        this.$forceUpdate()
      },
      
      handleResize() {
        // 防抖处理
        clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(() => {
          this.updateLayout()
        }, 100)
      },
      
      handleContainerClick(event) {
        // 如果点击的是容器而不是图片，不做任何处理
        if (event.target === this.$refs.photoWall) {
          console.log('点击了容器背景')
        }
      }
    }
  }
  </script>
  
  <style scoped lang="less">
  @import "~@/styles/global.less";
  
  .photo-wall-3d-container {
    position: relative;
    width: 100%;
    height: 700px;
    perspective: 1200px;
    overflow: hidden;
    background: linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 50%, #16213e 100%);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .photo-wall-3d {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transform: rotateX(v-bind('rotationX + "deg"')) rotateY(v-bind('rotationY + "deg"'));
    transition: transform 0.1s ease-out;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .photo-item {
    position: absolute;
    width: 280px;
    height: 280px;
    transform-style: preserve-3d;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    left: 50%;
    top: 50%;
    margin-left: -140px;
    margin-top: -140px;
    pointer-events: auto;
    
    &.active {
      transform: scale(1.1) !important;
      z-index: 3000 !important;
    }
    
    &:hover {
      transform: scale(1.08) !important;
    }
  }
  
  .photo-frame {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 0 2px rgba(0, 255, 255, 0.1),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    background: linear-gradient(145deg, #1e1e1e, #2a2a2a);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(45deg, 
        transparent 30%, 
        rgba(0, 255, 255, 0.1) 50%, 
        transparent 70%);
      opacity: 0;
      transition: opacity 0.3s ease;
      z-index: 2;
    }
    
    &:hover::before {
      opacity: 1;
    }
  }
  
  .photo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  .photo-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    color: white;
    padding: 20px 15px 15px;
    transform: translateY(100%);
    transition: transform 0.3s ease;
    z-index: 3;
  }
  
  .photo-item:hover .photo-overlay {
    transform: translateY(0);
  }
  
  .photo-title {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 5px;
    color: #00ffff;
  }
  
  .photo-description {
    font-size: 12px;
    opacity: 0.8;
  }
  
  .photo-glow {
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #00ffff, #ff00ff, #ffff00, #00ffff);
    border-radius: 17px;
    opacity: 0;
    z-index: -1;
    animation: glow-rotate 3s linear infinite;
  }
  
  .photo-item:hover .photo-glow {
    opacity: 0.7;
  }
  
  @keyframes glow-rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  // 预览模态框
  .preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(10px);
  }
  
  .preview-content {
    position: relative;
    max-width: 90%;
    max-height: 90%;
    background: linear-gradient(145deg, #1a1a2e, #16213e);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
    border: 2px solid rgba(0, 255, 255, 0.3);
  }
  
  .close-btn {
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    font-size: 24px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 10001;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 0, 0, 0.8);
      transform: scale(1.1);
    }
  }
  
  .nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 255, 255, 0.2);
    border: 2px solid #00ffff;
    color: white;
    font-size: 24px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 10001;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0, 255, 255, 0.4);
      transform: translateY(-50%) scale(1.1);
    }
  }
  
  .prev-btn {
    left: 20px;
  }
  
  .next-btn {
    right: 20px;
  }
  
  .preview-image-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  
  .preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  }
  
  .preview-info {
    padding: 20px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    text-align: center;
  }
  
  .preview-title {
    font-size: 24px;
    margin-bottom: 10px;
    color: #00ffff;
  }
  
  .preview-description {
    font-size: 16px;
    margin-bottom: 15px;
    opacity: 0.8;
  }
  
  .preview-counter {
    font-size: 14px;
    color: #888;
  }
  
  // 科技感背景
  .tech-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: hidden;
  }
  
  .tech-line {
    position: absolute;
    width: 2px;
    height: 100px;
    background: linear-gradient(to bottom, 
      transparent, 
      rgba(0, 255, 255, 0.8), 
      transparent);
    animation: tech-line-move linear infinite;
    opacity: 0.6;
  }
  
  @keyframes tech-line-move {
    0% {
      transform: translateY(-100px) rotate(0deg);
      opacity: 0;
    }
    10% {
      opacity: 0.6;
    }
    90% {
      opacity: 0.6;
    }
    100% {
      transform: translateY(100vh) rotate(360deg);
      opacity: 0;
    }
  }
  
  // 响应式设计
  @media (max-width: 768px) {
    .photo-wall-3d-container {
      height: 400px;
    }
    
    .photo-item {
      width: 150px;
      height: 150px;
    }
    
    .preview-content {
      max-width: 95%;
      max-height: 95%;
    }
    
    .preview-image {
      max-height: 60vh;
    }
    
    .nav-btn {
      width: 40px;
      height: 40px;
      font-size: 20px;
    }
    
    .prev-btn {
      left: 10px;
    }
    
    .next-btn {
      right: 10px;
    }
  }
  </style>